/** Bestandstabelle je Variante mit aufklappbaren Lots. Alle Aktionen kommen als Callback herein. */
import { Fragment } from "react";
import { eur } from "@/lib/api";
import { useI18n } from "@/i18n";
import { cn } from "@/lib/utils";
import { Th, Td, SortTh } from "@/components/common";
import { Button } from "@/components/ui/button";
import { AlertTriangle, History, Pencil, PackageMinus, Megaphone, ChevronDown, ChevronRight } from "lucide-react";
import InventoryLotRows from "./InventoryLotRows";

export const dimStr = (r) => {
  const u = r.dimension_uom || "cm";
  if (r.diameter_cm) return `⌀ ${r.diameter_cm} ${u}`;
  const p = [r.length_cm, r.width_cm, r.height_cm].filter((x) => x != null && x !== "");
  return p.length ? `${p.join("×")} ${u}` : "—";
};

const SupplierCell = ({ row, group }) => {
  const { t } = useI18n();
  if (group && group.supplier_count) {
    return group.supplier_count > 1
      ? <span className="text-muted-foreground">{t("ex.supplierMultiple")}</span>
      : group.supplier_name;
  }
  if (row.master_supplier_name) {
    return <span className="italic text-muted-foreground" title={t("ex.supplierFromMaster")}
      data-testid={`supplier-master-${row.sku}`}>{row.master_supplier_name}</span>;
  }
  return "—";
};

export default function InventoryTable({ rows, lotGroups, expanded, setExpanded, canEdit,
  canRename, canEditBbd, canCost, locEnabled, qLabelOf, onHistory, onOwn, onPromo, onAdjust, onTransfer,
  onWriteOff, onBbd, onEditRow, sortKey, sortDir, onSort }) {
  const { t } = useI18n();
  const sortTh = (id, label, right) => (
    <SortTh sticky sortId={id} right={right} active={sortKey === id} dir={sortDir} onSort={onSort}>{label}</SortTh>
  );

  return (
    <div className="flex-1 min-h-0 overflow-auto rounded-md border border-border bg-card shadow-sm">
      <table className="w-full">
        <thead className="bg-muted/50"><tr>
          {sortTh("product", t("inv.productSku"))}
          <Th sticky>{t("prod.dimensions")}</Th>
          <Th sticky>{t("ex.supplier")}</Th>
          {sortTh("physical", t("inv.physical"), true)}
          {sortTh("reserved", t("inv.reserved"), true)}
          {sortTh("available", t("inv.available"), true)}
          {sortTh("defect", t("quality.defect"), true)}
          {sortTh("second_choice", t("quality.second_choice"), true)}
          {canCost && sortTh("stock_value", t("common.stockValue"), true)}
          <Th sticky></Th>
        </tr></thead>
        <tbody data-testid="inventory-table">
          {rows.map((r) => {
            const g = lotGroups[r.variant_id];
            const isOpen = !!expanded[r.variant_id];
            return (
              <Fragment key={r.variant_id}>
                <tr className={cn("hover:bg-muted/40 transition-colors", r.understock && "bg-destructive/5")}>
                  <Td><div className="flex items-start gap-1.5">
                    {g && g.lots.length > 0 ? (
                      <button type="button" title={t("ex.showLots")} data-testid={`lots-toggle-${r.sku}`}
                        className="mt-0.5 text-muted-foreground hover:text-foreground"
                        onClick={() => setExpanded({ ...expanded, [r.variant_id]: !isOpen })}>
                        {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </button>
                    ) : (
                      // Platzhalter: Artikelnamen stehen mit und ohne Aufklapp-Pfeil auf einer Linie.
                      <span className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                    )}
                    <div>
                      <div className="font-medium text-foreground">{r.product_name}
                        {r.understock && <AlertTriangle className="inline h-3.5 w-3.5 ml-1.5 text-destructive" />}</div>
                      <div className="text-xs text-muted-foreground font-mono">{r.sku}</div>
                    </div>
                  </div></Td>
                  <Td mono className={r.is_digital ? "text-muted-foreground" : (dimStr(r) === "—" ? "text-chart-3" : "")}>
                    {r.is_digital ? t("common.digital") : dimStr(r)}</Td>
                  <Td data-testid={`supplier-cell-${r.sku}`}><SupplierCell row={r} group={g} /></Td>
                  <Td right mono data-testid={`physical-${r.sku}`}>{r.physical}</Td>
                  <Td right mono>{r.reserved}</Td>
                  <Td right mono className={cn("font-semibold", r.available <= 0 && "text-destructive")}>{r.available}</Td>
                  <Td right mono>{r.defect}</Td>
                  <Td right mono data-testid={`second-${r.sku}`}>{r.second_choice}</Td>
                  {canCost && <Td right mono>{eur(r.stock_value)}</Td>}
                  <Td right>
                    <div className="flex justify-end gap-1">
                      {(canRename || canEdit) && <Button size="sm" variant="ghost" title={t("inv.editRowTitle")} onClick={() => onEditRow(r)} data-testid={`rename-${r.sku}`}><Pencil className="h-4 w-4" /></Button>}
                      <Button size="sm" variant="ghost" onClick={() => onHistory(r)} data-testid={`hist-${r.sku}`}><History className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" title={t("inv.ownWithdrawal")} onClick={() => onOwn(r)} data-testid={`ownw-book-${r.sku}`}><PackageMinus className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" title={t("inv.promotion")} onClick={() => onPromo(r)} data-testid={`promo-book-${r.sku}`}><Megaphone className="h-4 w-4" /></Button>
                      {canEdit && <Button size="sm" variant="outline" onClick={() => onAdjust(r)} data-testid={`adjust-${r.sku}`}>{t("inv.correction")}</Button>}
                      {canEdit && locEnabled && g && g.lots.length > 0 && (
                        <Button size="sm" variant="outline" data-testid={`transfer-${r.sku}`}
                          onClick={() => onTransfer({ variant: r, lots: g.lots })}>{t("tr.transfer")}</Button>)}
                    </div>
                  </Td>
                </tr>
                {isOpen && g && (
                  <InventoryLotRows row={r} group={g} locEnabled={locEnabled} canEdit={canEdit}
                    canEditBbd={canEditBbd} qLabelOf={qLabelOf} onTransfer={onTransfer}
                    onBbd={onBbd} onWriteOff={onWriteOff} />
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
